import { UserIcon, UsersIcon } from '@music-vine/cadence/icons';
import type React from 'react';
import { useCallback, useMemo } from 'react';
import { useCurrentPrice } from '@/hooks/api/useCurrentPrice';
import { usePricingOptions } from '@/hooks/api/usePricingOptions';
import { useIsLoggedIn, useSubscriptionType } from '@/hooks/auth';
import useBillingMode from '@/hooks/billing/useBillingMode';
import { useCurrency } from '@/hooks/billing/useCurrency';
import type { PricingCardBulletPoint } from '@/components/PricingPage/types';
import type { PricingOption } from '@/hooks/api/usePricingOptions';
import { getFallbackPrice } from '@/data/pricing-fallbacks';
import { isBusinessPlanName } from '@/lib/pricing/businessPlans';
import { LICENSE_TOOLTIPS } from '@/lib/pricing/planCopy';
import { useTaxExclusiveNote } from '@/lib/pricing/useTaxExclusiveNote';
import type { FallbackPlanKey } from '@/data/pricing-fallbacks';
import type { BillingMode } from '@/atoms/billing';

// Define Feature Section structure
export interface FeatureSection {
  items: PricingCardBulletPoint[];
  title: string;
}

// Update PricingPlan interface
export interface PricingPlan {
  /** Label of the floating pill on the highlighted card. */
  badgeLabel?: string;
  /** Short pitch under the price. Absent on the 2026 card designs. */
  description?: string;
  featureSections: FeatureSection[]; // Use the new structure
  isPopular?: boolean;
  period: string;
  price: number | string;
  /** Line under the price, in place of the yearly-saving line. */
  priceNote?: string;
  subtitle: string;
  subtitleIcon?: React.ReactNode;
  title: string;
  type: 'Free' | 'Essentials' | 'Creator' | 'Pro' | 'Business';
}

/**
 * Finds matching pricing option from API data by plan title
 */
const findPricingOptionByTitle = (options: PricingOption[] | undefined, title: string): PricingOption | undefined => options?.find(option => option.title.toLowerCase() === title.toLowerCase());
export interface UsePricingCardsOptions {
  /** Restrict (and order) the plans returned. 'Free' only exists as a table column. */
  planTypes?: PricingPlan['type'][];
}
export const usePricingCards = (fallbackCurrencyCode = 'usd', billingModeOverride?: BillingMode, {
  planTypes
}: UsePricingCardsOptions = {}) => {
  const {
    selectedBillingMode
  } = useBillingMode();

  // Screens that must show a specific period (e.g. the change-plan picker,
  // pinned to the subscriber's real billing frequency) pass an override;
  // everywhere else follows the global pricing-page toggle.
  const billingMode = billingModeOverride ?? selectedBillingMode;
  const currency = useCurrency();
  const taxNote = useTaxExclusiveNote();
  const {
    data: pricingOptions,
    isError: isPricingError,
    refetch: refetchPricing
  } = usePricingOptions();
  const isLoggedIn = useIsLoggedIn();
  const subscriptionType = useSubscriptionType();
  const currencyCode = currency?.code?.toLowerCase() ?? fallbackCurrencyCode.toLowerCase();
  const isPaidSubscriber = isLoggedIn && subscriptionType !== 'Free';
  const {
    data: currentPriceResponse,
    isPending: isCurrentPricePending,
    isError: isCurrentPriceError
  } = useCurrentPrice({
    enabled: isPaidSubscriber
  });
  const currentPrice = currentPriceResponse?.data;
  const subscriberOverride = useMemo(() => {
    if (!isPaidSubscriber || isCurrentPriceError || !currentPrice) {
      return null;
    }
    const frequencyMatchesToggle = currentPrice.frequency === 'year' && billingMode === 'yearly' || currentPrice.frequency === 'month' && billingMode === 'monthly';
    const currencyMatches = currentPrice.currency?.toLowerCase() === currencyCode;
    if (!(frequencyMatchesToggle && currencyMatches)) {
      return null;
    }
    const displayPrice = currentPrice.frequency === 'year' ? Math.round(currentPrice.amount / 12 * 100) / 100 : currentPrice.amount;
    return {
      planType: subscriptionType,
      price: displayPrice
    };
  }, [isPaidSubscriber, isCurrentPriceError, currentPrice, billingMode, currencyCode, subscriptionType]);
  const pricingPlans = useMemo<PricingPlan[]>(() => {
    const isPeriodYearly = billingMode === 'yearly';

    /**
     * Cards always render `/month`. In yearly mode that means the
     * monthly-equivalent of the yearly price (yearly / 12). The API path
     * below does this division explicitly; the fallback needs to do the
     * same so the period toggle actually changes the displayed price for
     * currencies the API doesn't return data for.
     */
    const fallbackDisplayPrice = (plan: FallbackPlanKey) => {
      if (isPeriodYearly) {
        const yearly = getFallbackPrice(plan, currencyCode, 'yearly');
        return Math.round(yearly / 12 * 100) / 100;
      }
      return getFallbackPrice(plan, currencyCode, 'monthly');
    };

    /**
     * The 2026 plan cards (DEV-6397): no Free card, audience line instead of
     * a description, license-led feature sections, Pro highlighted as Best
     * value.
     */
    const plans: PricingPlan[] = [{
      type: 'Free',
      title: 'Free',
      subtitle: 'Individual creators',
      subtitleIcon: <UserIcon className="h-4 w-4" />,
      price: 0,
      period: '/month',
      featureSections: []
    }, {
      type: 'Essentials',
      title: 'Essentials',
      subtitle: 'Individual creators',
      subtitleIcon: <UserIcon className="h-4 w-4" />,
      price: fallbackDisplayPrice('essentials'),
      period: '/month',
      featureSections: [{
        title: 'License for',
        items: [{
          content: 'Individuals publishing on their own channels'
        }]
      }, {
        title: 'Unlimited access to',
        items: [{
          content: 'Music'
        }, {
          content: 'Sound effects'
        }]
      }, {
        title: 'Additional features',
        items: [{
          content: 'Safelist 1 YouTube channel'
        }]
      }]
    }, {
      type: 'Creator',
      title: 'Creator',
      subtitle: 'Individual creators',
      subtitleIcon: <UserIcon className="h-4 w-4" />,
      price: fallbackDisplayPrice('creator'),
      period: '/month',
      featureSections: [{
        title: 'License for',
        items: [{
          content: 'Individuals publishing on their own channels'
        }]
      }, {
        title: 'Unlimited access to',
        items: [{
          content: 'Music'
        }, {
          content: 'Sound effects'
        }, {
          content: 'Stock video (up to 1080p)'
        }, {
          content: 'Motion graphics'
        }, {
          content: 'LUTs'
        }]
      }, {
        title: 'Additional features',
        items: [{
          content: 'Safelist 3 YouTube channels'
        }]
      }]
    }, {
      type: 'Pro',
      title: 'Pro',
      subtitle: 'Freelancers & power creators',
      subtitleIcon: <UserIcon className="h-4 w-4" />,
      price: fallbackDisplayPrice('pro'),
      period: '/month',
      isPopular: true,
      badgeLabel: 'POPULAR',
      featureSections: [{
        title: 'License for',
        items: [{
          content: 'Individuals, freelancers and solo professionals'
        }, {
          content: 'Your own content and client projects'
        }, {
          content: 'Commercial digital use',
          tooltip: LICENSE_TOOLTIPS.proDigitalUse
        }]
      }, {
        title: 'Unlimited access to',
        items: [{
          content: 'Music'
        }, {
          content: 'Sound effects'
        }, {
          content: 'Stock video (up to 4K)'
        }, {
          content: 'Motion graphics'
        }, {
          content: 'LUTs'
        }]
      }, {
        title: 'Additional features',
        items: [{
          content: 'Safelist 10 YouTube channels'
        }, {
          content: 'High-quality WAV files for music and sound effects'
        }]
      }]
    }, {
      type: 'Business',
      title: 'Business',
      subtitle: 'Organizations, brands & teams',
      subtitleIcon: <UsersIcon className="h-4 w-4" />,
      price: fallbackDisplayPrice('business'),
      period: `/month ${taxNote}`,
      featureSections: [{
        title: 'License for',
        items: [{
          content: 'Teams, companies and organizations'
          // content: 'Organizations: teams, agencies & companies',
        }, {
          content: 'Company, client and commercial projects'
        }, {
          content: 'Commercial digital use',
          tooltip: LICENSE_TOOLTIPS.businessDigitalUse
        }]
      }, {
        title: 'Unlimited access to',
        items: [{
          content: 'Music'
        }, {
          content: 'Sound effects'
        }, {
          content: 'Stock video (up to 4K)'
        }, {
          content: 'Motion graphics'
        }, {
          content: 'LUTs'
        }]
      }, {
        title: 'Additional features',
        items: [{
          content: 'Safelist 10 YouTube channels'
        }, {
          content: 'High-quality WAV files for music and sound effects'
        }]
      }]
    }];

    // Business subscribers have nothing above them to sell, so no plan is
    // highlighted as Popular for them.
    const visiblePlans = plans
    // Free is a header-only column for /pricing/business; never a card.
    .filter(plan => planTypes?.includes(plan.type) ?? plan.type !== 'Free').map(plan => subscriptionType === 'Business' ? {
      ...plan,
      isPopular: false,
      badgeLabel: undefined
    } : plan);
    const withApiPricing = pricingOptions?.length ? visiblePlans.map(plan => {
      const apiPricingOption = findPricingOptionByTitle(pricingOptions, plan.title);
      if (apiPricingOption) {
        const pricing = apiPricingOption?.pricing?.[currencyCode as keyof typeof apiPricingOption.pricing];
        if (pricing) {
          // Business keeps its own period for the "excl. tax" note.
          const isBusiness = isBusinessPlanName(plan.type);
          let price = isPeriodYearly ? pricing.yearly : pricing.monthly;
          if (isPeriodYearly) {
            price /= 12;
            price = Math.round(price * 100) / 100;
          }
          return {
            ...plan,
            price,
            period: isBusiness ? plan.period : '/month'
          };
        }
      }
      return plan;
    }) : visiblePlans;
    if (!subscriberOverride) {
      return withApiPricing;
    }
    return withApiPricing.map(plan =>
    // Keep the plan's own period — every plan is quoted monthly by now, and
    // overwriting it dropped Business's "excl. tax" for Business subscribers.
    plan.type === subscriberOverride.planType ? {
      ...plan,
      price: subscriberOverride.price
    } : plan);
  }, [billingMode, currencyCode, pricingOptions, subscriberOverride, subscriptionType, planTypes, taxNote]);
  const isActualPriceLoading = useCallback((type: PricingPlan['type']) => isPaidSubscriber && type === subscriptionType && isCurrentPricePending, [isPaidSubscriber, subscriptionType, isCurrentPricePending]);
  return {
    pricingPlans,
    isPricingError,
    refetchPricing,
    isActualPriceLoading
  };
};